import React, { useState } from 'react';
import { Twitter, Linkedin, Link2, Check, MessageCircle, Mail } from 'lucide-react';

/**
 * ShareButtons - X / LinkedIn / WhatsApp / Email / copy-link with toast.
 *
 * Renders inline beneath the article excerpt. Uses the canonical mundalo.com
 * share URL so X/LinkedIn previews work correctly even when the page is
 * being previewed from a non-production environment.
 */
const ShareButtons = ({ title, slug }) => {
  const [copied, setCopied] = useState(false);
  const url = `https://mundalo.com/relo-news/${slug}`;
  const text = title || 'From the Mundalo Dispatch';

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) { /* noop */ }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url + '?utm_source=mundalo-share&utm_medium=twitter&utm_campaign=article-' + slug)}`;
  const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url + '?utm_source=mundalo-spotlight&utm_medium=linkedin&utm_campaign=mover-' + slug)}`;
  // WhatsApp wa.me deeplink (works on mobile + WhatsApp Web)
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}?utm_source=mundalo-share&utm_medium=whatsapp&utm_campaign=article-${slug}`)}`;
  // Mailto with prefilled subject + body
  const emailHref = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`Thought you'd find this useful:\n\n${url}?utm_source=mundalo-share&utm_medium=email&utm_campaign=article-${slug}\n\nFrom Mundalo's Relo News`)}`;

  const iconBtn = "w-9 h-9 flex items-center justify-center rounded-full border border-[#0A1F2E]/15 text-[#0A1F2E]/65 hover:bg-[#0A1F2E] hover:text-[#F9F6EF] hover:border-[#0A1F2E] transition";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="article-share">
      <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mr-1">
        Share
      </span>

      <a
        href={twitterHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
        className={iconBtn}
        data-testid="article-share-twitter"
      >
        <Twitter className="w-4 h-4" />
      </a>

      <a
        href={linkedInHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
        className={iconBtn}
        data-testid="article-share-linkedin"
      >
        <Linkedin className="w-4 h-4" />
      </a>

      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on WhatsApp"
        className={iconBtn}
        data-testid="article-share-whatsapp"
      >
        <MessageCircle className="w-4 h-4" />
      </a>

      <a
        href={emailHref}
        aria-label="Share via email"
        className={iconBtn}
        data-testid="article-share-email"
      >
        <Mail className="w-4 h-4" />
      </a>

      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy link"
        className={`w-9 h-9 flex items-center justify-center rounded-full border transition ${
          copied
            ? 'bg-[#2CA8A0] border-[#2CA8A0] text-white'
            : 'border-[#0A1F2E]/15 text-[#0A1F2E]/65 hover:bg-[#0A1F2E] hover:text-[#F9F6EF] hover:border-[#0A1F2E]'
        }`}
        data-testid="article-share-copy"
      >
        {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </button>

      {copied && (
        <span
          className="ml-2 font-mono uppercase text-[10px] tracking-[0.15em] text-[#2CA8A0] font-semibold"
          role="status"
          data-testid="article-share-copied-toast"
        >
          ✓ Link copied
        </span>
      )}
    </div>
  );
};

export default ShareButtons;
